import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Search, X, ArrowRight, ArrowLeft } from 'lucide-react';
import client from '../api/client';

export default function HevyImportWizard({ onComplete }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState({});       // hevy_name → exercisedb_id
  const [skipped, setSkipped] = useState(new Set());
  const [searchQueries, setSearchQueries] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [importResult, setImportResult] = useState(null);

  // Drag-and-drop state
  const [dragOver, setDragOver] = useState(false);

  // ---- Step 1: Upload ----

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
    } else {
      toast.error(t('import.invalidFile', 'Please select a valid CSV file'));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleUploadAndPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await client.post('/import/hevy/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data;
      setPreview(data);

      // Auto-fill mappings from matched_exercises
      const autoMappings = {};
      if (data.matched_exercises) {
        for (const match of data.matched_exercises) {
          const exId = match.exercisedb_id || match.matched_exercise?.id;
          if (exId) {
            autoMappings[match.hevy_name] = exId;
          }
        }
      }
      setMappings(autoMappings);
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.detail || t('import.uploadError', 'Failed to process CSV file');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---- Step 2: Review & Map ----

  const handleSearch = async (hevyName, query) => {
    setSearchQueries((prev) => ({ ...prev, [hevyName]: query }));
    if (!query || query.length < 2) {
      setSearchResults((prev) => ({ ...prev, [hevyName]: [] }));
      return;
    }
    try {
      const res = await client.get(`/exercises?search=${encodeURIComponent(query)}&limit=10`);
      setSearchResults((prev) => ({ ...prev, [hevyName]: res.data }));
    } catch {
      // Silently fail search
    }
  };

  const handleMapExercise = (hevyName, exerciseId) => {
    setMappings((prev) => ({ ...prev, [hevyName]: exerciseId }));
    setSearchQueries((prev) => ({ ...prev, [hevyName]: '' }));
    setSearchResults((prev) => ({ ...prev, [hevyName]: [] }));
    setSkipped((prev) => {
      const next = new Set(prev);
      next.delete(hevyName);
      return next;
    });
  };

  const handleSkip = (hevyName) => {
    setSkipped((prev) => new Set(prev).add(hevyName));
    setMappings((prev) => {
      const next = { ...prev };
      delete next[hevyName];
      return next;
    });
  };

  const handleUndoSkip = (hevyName) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      next.delete(hevyName);
      return next;
    });
  };

  const getUnmatchedExercises = () => {
    if (!preview?.unmatched_exercises) return [];
    return preview.unmatched_exercises;
  };

  const allResolved = () => {
    const unmatched = getUnmatchedExercises();
    return unmatched.every((ex) => mappings[ex.hevy_name] || skipped.has(ex.hevy_name));
  };

  const getAutoMatchedCount = () => {
    if (!preview?.matched_exercises) return 0;
    return preview.matched_exercises.filter((m) => m.exercisedb_id || m.matched_exercise?.id).length;
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const res = await client.post('/import/hevy/confirm', {
        sessions: preview.sessions,
        exercise_mappings: mappings,
        skipped_exercises: Array.from(skipped),
      });
      setImportResult(res.data);
      setStep(3);
      toast.success(t('import.importSuccess', 'Workouts imported successfully!'));
    } catch (err) {
      const msg = err.response?.data?.detail || t('import.importError', 'Failed to import workouts');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---- Render ----

  const headingStyle = { fontFamily: 'Barlow Condensed, sans-serif' };

  // Step 1: Upload
  if (step === 1) {
    return (
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle style={headingStyle} className="text-xl">
            {t('import.uploadTitle', 'Upload Hevy Export')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-primary bg-primary/10'
                : 'border-border/50 hover:border-primary/50'
            }`}
          >
            <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="font-medium">{t('import.dropHere', 'Drop your Hevy CSV file here')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('import.orClick', 'or click to browse')}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files[0])}
          />

          {/* File card */}
          {file && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
              <FileSpreadsheet className="w-8 h-8 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="p-1 rounded hover:bg-muted active:bg-muted"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          )}

          {/* Hint */}
          <p className="text-xs text-muted-foreground">
            {t('import.hevyHint', 'In Hevy: Profile → Settings → Export Data → Download CSV')}
          </p>

          {/* Next button */}
          <Button
            onClick={handleUploadAndPreview}
            disabled={!file || loading}
            className="w-full bg-primary hover:bg-primary/90 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            {loading
              ? t('import.processing', 'Processing...')
              : t('import.next', 'Next')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Step 2: Review & Map
  if (step === 2) {
    const unmatched = getUnmatchedExercises();
    const autoMatched = getAutoMatchedCount();

    return (
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle style={headingStyle} className="text-xl">
            {t('import.reviewTitle', 'Review & Map Exercises')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-2xl font-bold text-primary">{preview?.session_count || 0}</p>
              <p className="text-sm text-muted-foreground">{t('import.sessions', 'Sessions')}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-2xl font-bold text-primary">{preview?.exercise_count || 0}</p>
              <p className="text-sm text-muted-foreground">{t('import.exercises', 'Exercises')}</p>
            </div>
          </div>

          {/* Date range */}
          {preview?.date_range && (
            <p className="text-sm text-muted-foreground text-center">
              {preview.date_range.earliest} — {preview.date_range.latest}
            </p>
          )}

          {/* Auto-matched count */}
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span>
              {t('import.autoMatched', '{{count}} exercises auto-matched', { count: autoMatched })}
            </span>
          </div>

          {/* Unmatched exercises list */}
          {unmatched.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                {t('import.unmatchedTitle', '{{count}} exercises need mapping', { count: unmatched.length })}
              </p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {unmatched.map((ex) => {
                  const isMapped = !!mappings[ex.hevy_name];
                  const isSkipped = skipped.has(ex.hevy_name);

                  return (
                    <div
                      key={ex.hevy_name}
                      className={`p-3 rounded-lg border text-sm ${
                        isMapped
                          ? 'border-green-500/30 bg-green-500/5'
                          : isSkipped
                          ? 'border-border/30 bg-muted/30 opacity-60'
                          : 'border-amber-500/30 bg-amber-500/5'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{ex.hevy_name}</span>
                        <span className="text-xs">
                          {isMapped && (
                            <span className="text-green-500">{t('import.mapped', 'Mapped')}</span>
                          )}
                          {isSkipped && (
                            <span className="text-muted-foreground">{t('import.skipped', 'Skipped')}</span>
                          )}
                          {!isMapped && !isSkipped && (
                            <span className="text-amber-500">{t('import.needsMapping', 'Needs mapping')}</span>
                          )}
                        </span>
                      </div>

                      {/* Suggestion link */}
                      {ex.best_guess && !isMapped && !isSkipped && (
                        <button
                          onClick={() => handleMapExercise(ex.hevy_name, ex.best_guess.id)}
                          className="text-xs text-primary hover:underline mb-2 block"
                        >
                          {t('import.useSuggestion', 'Use suggestion:')} {ex.best_guess.name}{' '}
                          ({ex.best_score || 0}%)
                        </button>
                      )}

                      {/* Search input */}
                      {!isMapped && !isSkipped && (
                        <div className="relative">
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                              <input
                                type="text"
                                placeholder={t('import.searchExercise', 'Search exercise...')}
                                value={searchQueries[ex.hevy_name] || ''}
                                onChange={(e) => handleSearch(ex.hevy_name, e.target.value)}
                                className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-border/50 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                              />
                            </div>
                            <button
                              onClick={() => handleSkip(ex.hevy_name)}
                              className="text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 border border-border/50 rounded"
                            >
                              {t('import.skip', 'Skip')}
                            </button>
                          </div>

                          {/* Search results dropdown */}
                          {searchResults[ex.hevy_name]?.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-background border border-border/50 rounded-lg shadow-lg max-h-[150px] overflow-y-auto">
                              {searchResults[ex.hevy_name].map((result) => (
                                <button
                                  key={result.id || result._id}
                                  onClick={() => handleMapExercise(ex.hevy_name, result.id || result._id)}
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 border-b border-border/20 last:border-0 flex items-center gap-2"
                                >
                                  {(result.gifUrl || result.image_url) && (
                                    <img src={result.gifUrl || result.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                  )}
                                  <div>
                                    <span className="font-medium">{result.name}</span>
                                    {result.equipment && (
                                      <span className="text-muted-foreground ml-1">({result.equipment})</span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Undo skip */}
                      {isSkipped && (
                        <button
                          onClick={() => handleUndoSkip(ex.hevy_name)}
                          className="text-xs text-primary hover:underline"
                        >
                          {t('import.undoSkip', 'Undo skip')}
                        </button>
                      )}

                      {/* Undo mapping */}
                      {isMapped && (
                        <button
                          onClick={() => {
                            setMappings((prev) => {
                              const next = { ...prev };
                              delete next[ex.hevy_name];
                              return next;
                            });
                          }}
                          className="text-xs text-muted-foreground hover:underline"
                        >
                          {t('import.changeMapping', 'Change mapping')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('import.back', 'Back')}
            </Button>
            <Button
              onClick={handleImport}
              disabled={!allResolved() || loading}
              className="flex-1 bg-primary hover:bg-primary/90 text-white"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              {loading
                ? t('import.importing', 'Importing...')
                : t('import.importBtn', 'Import')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Step 3: Success
  if (step === 3) {
    return (
      <Card className="border-border/50">
        <CardContent className="py-8 text-center space-y-4">
          <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
          <h2 className="text-2xl font-bold" style={headingStyle}>
            {t('import.complete', 'Import Complete!')}
          </h2>

          <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-2xl font-bold text-primary">
                {importResult?.imported_sessions || 0}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('import.importedSessions', 'Sessions')}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-2xl font-bold text-primary">
                {importResult?.prs_detected || 0}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('import.prsDetected', 'PRs Detected')}
              </p>
            </div>
          </div>

          <Button
            onClick={() => onComplete?.()}
            className="bg-primary hover:bg-primary/90 text-white mt-4"
          >
            {t('import.done', 'Done')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}
